/**
 * Hook for player categorisation and ranking data in competitive sessions.
 * Manages category state, calculation, persistence, and ranking sections for the Player Ranking tab.
 */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { categorizePlayersForSession } from '../utils/playerCategorizationUtils';
import { getGenderTextColor } from '../utils/genderColors';

const CATEGORY_MIN_ACTIVE_PLAYERS = 5;
const CATEGORY_ELO_DELTA_THRESHOLD = 50;

const createDefaultCategoryMeta = (overrides = {}) => ({
  categories: {},
  categoryRanges: {},
  categoryCount: 0,
  lastCalculated: null,
  disabledReason: 'not_calculated',
  stats: null,
  playerCount: 0,
  method: null,
  ...overrides,
});

const formatEloValue = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Math.round(value);
};

export default function usePlayerCategorisation({
  sessionId,
  sessionLogger,
  playersById = {},
  sessionPlayerStatus = {},
  gamesPlayedCount = {},
  initialCategorizationEnabled = false,
}) {
  const [categorizationEnabled, setCategorizationEnabled] = useState(initialCategorizationEnabled);
  const [playerCategories, setPlayerCategories] = useState({
    mixed: {},
    levels: {},
    levels_male: {},
    levels_female: {},
  });
  const [categoryMetadata, setCategoryMetadata] = useState({
    mixed: createDefaultCategoryMeta(),
    levels: createDefaultCategoryMeta(),
    levels_male: createDefaultCategoryMeta(),
    levels_female: createDefaultCategoryMeta(),
  });
  const [eloChangeTracker, setEloChangeTracker] = useState({});
  const [isCategoryRefreshing, setIsCategoryRefreshing] = useState(false);
  const [rankingVisibility, setRankingVisibility] = useState({
    mixed: false,
    male: false,
    female: false,
  });
  const [rankingTab, setRankingTab] = useState('mixed');

  const categorizationEnabledRef = useRef(categorizationEnabled);
  const playerCategoriesRef = useRef(playerCategories);
  const lastCategoryAutoRefreshThresholdRef = useRef(0);

  useEffect(() => {
    categorizationEnabledRef.current = categorizationEnabled;
  }, [categorizationEnabled]);
  useEffect(() => {
    playerCategoriesRef.current = playerCategories;
  }, [playerCategories]);

  useEffect(() => {
    if (!categorizationEnabled) {
      const empty = { mixed: {}, levels: {}, levels_male: {}, levels_female: {} };
      setPlayerCategories(empty);
      playerCategoriesRef.current = empty;
      setCategoryMetadata({
        mixed: createDefaultCategoryMeta({ disabledReason: 'disabled' }),
        levels: createDefaultCategoryMeta({ disabledReason: 'disabled' }),
        levels_male: createDefaultCategoryMeta({ disabledReason: 'disabled' }),
        levels_female: createDefaultCategoryMeta({ disabledReason: 'disabled' }),
      });
      setEloChangeTracker({});
    }
  }, [categorizationEnabled]);

  const getCategorizationPlayers = useCallback(() => {
    if (!playersById || typeof playersById !== 'object') return [];
    const excluded = new Set(['inactive', 'removed', 'left', 'exited']);
    return Object.values(playersById).filter((p) => {
      if (!p?.id) return false;
      const status = (sessionPlayerStatus[p.id] || '').toLowerCase();
      return status && !excluded.has(status);
    });
  }, [playersById, sessionPlayerStatus]);

  const computeAverageGamesPlayed = useCallback(() => {
    const active = getCategorizationPlayers();
    if (!active.length) return 0;
    const total = active.reduce((sum, p) => {
      const c = gamesPlayedCount[p.id] ?? p.effectiveGamesPlayed ?? p.gamesPlayedThisSession ?? 0;
      return sum + c;
    }, 0);
    return total / active.length;
  }, [getCategorizationPlayers, gamesPlayedCount]);

  const captureEloSnapshot = useCallback(
    (playersList = null) => {
      const source = playersList || getCategorizationPlayers();
      const snapshot = {};
      source.forEach((p) => {
        if (!p?.id) return;
        snapshot[p.id] = {
          mixed: typeof p.mixed_elo_rating === 'number' ? p.mixed_elo_rating : 1200,
          levels: typeof p.levels_elo_rating === 'number' ? p.levels_elo_rating : 1200,
        };
      });
      return snapshot;
    },
    [getCategorizationPlayers]
  );

  const buildCategoryMetadata = useCallback((result, fallbackReason = null) => {
    if (!result) {
      return createDefaultCategoryMeta({
        disabledReason: fallbackReason || 'not_enough_players',
        lastCalculated: new Date().toISOString(),
      });
    }
    return createDefaultCategoryMeta({
      categoryRanges: result.categoryRanges || {},
      categoryCount: result.categoryCount || 0,
      lastCalculated: result.lastCalculated || new Date().toISOString(),
      disabledReason: result.disabledReason || null,
      stats: result.stats || null,
      playerCount: result.playerCount || 0,
      method: result.method || null,
    });
  }, []);

  const buildCombinedLevelsMetadata = useCallback((maleMeta, femaleMeta) => ({
    ...createDefaultCategoryMeta({
      categoryCount: Math.max(maleMeta.categoryCount || 0, femaleMeta.categoryCount || 0),
      lastCalculated: maleMeta.lastCalculated || femaleMeta.lastCalculated,
      disabledReason: maleMeta.disabledReason && femaleMeta.disabledReason ? 'not_enough_players' : null,
      playerCount: (maleMeta.playerCount || 0) + (femaleMeta.playerCount || 0),
    }),
    genderBreakdown: { male: maleMeta, female: femaleMeta },
  }), []);

  const shouldRecalculateCategories = useCallback(() => {
    if (!categorizationEnabled) return false;
    const active = getCategorizationPlayers();
    if (active.length < CATEGORY_MIN_ACTIVE_PLAYERS) {
      const has = Object.keys(playerCategories.mixed || {}).length > 0 ||
        Object.keys(playerCategories.levels || {}).length > 0 ||
        Object.keys(playerCategories.levels_male || {}).length > 0 ||
        Object.keys(playerCategories.levels_female || {}).length > 0;
      return has;
    }
    if (!eloChangeTracker || Object.keys(eloChangeTracker).length === 0) return true;
    const current = captureEloSnapshot(active);
    if (Object.keys(current).length !== Object.keys(eloChangeTracker).length) return true;
    for (const [playerId, snapshot] of Object.entries(current)) {
      const prev = eloChangeTracker[playerId];
      if (!prev) return true;
      if (Math.abs(snapshot.mixed - prev.mixed) >= CATEGORY_ELO_DELTA_THRESHOLD ||
          Math.abs(snapshot.levels - prev.levels) >= CATEGORY_ELO_DELTA_THRESHOLD) return true;
    }
    return false;
  }, [categorizationEnabled, getCategorizationPlayers, playerCategories, eloChangeTracker, captureEloSnapshot]);

  const calculatePlayerCategories = useCallback((force = false) => {
    if (!categorizationEnabled && !force) return;
    const active = getCategorizationPlayers();
    if (active.length < CATEGORY_MIN_ACTIVE_PLAYERS) {
      const empty = { mixed: {}, levels: {}, levels_male: {}, levels_female: {} };
      setPlayerCategories(empty);
      playerCategoriesRef.current = empty;
      setCategoryMetadata({
        mixed: buildCategoryMetadata(null, 'not_enough_players'),
        levels: buildCategoryMetadata(null, 'not_enough_players'),
        levels_male: buildCategoryMetadata(null, 'not_enough_players'),
        levels_female: buildCategoryMetadata(null, 'not_enough_players'),
      });
      setEloChangeTracker({});
      return;
    }
    if (!force && !shouldRecalculateCategories()) return;

    setIsCategoryRefreshing(true);
    try {
      const result = categorizePlayersForSession(active, {
        minPlayers: CATEGORY_MIN_ACTIVE_PLAYERS,
        minCategorySize: 2,
        maxCategories: 4,
      });
      const mixedResult = result.mixed;
      const levelsMaleResult = result.levels_male;
      const levelsFemaleResult = result.levels_female;

      const newCategories = {
        mixed: mixedResult?.categories || {},
        levels: {
          ...(levelsMaleResult?.categories || {}),
          ...(levelsFemaleResult?.categories || {}),
        },
        levels_male: levelsMaleResult?.categories || {},
        levels_female: levelsFemaleResult?.categories || {},
      };
      setPlayerCategories(newCategories);
      playerCategoriesRef.current = newCategories;

      const mixedMeta = buildCategoryMetadata(mixedResult);
      const maleMeta = buildCategoryMetadata(levelsMaleResult, 'not_enough_players');
      const femaleMeta = buildCategoryMetadata(levelsFemaleResult, 'not_enough_players');
      setCategoryMetadata({
        mixed: mixedMeta,
        levels: buildCombinedLevelsMetadata(maleMeta, femaleMeta),
        levels_male: maleMeta,
        levels_female: femaleMeta,
      });
      setEloChangeTracker(captureEloSnapshot(active));

      if (sessionLogger?.shouldUseNormalizedState?.() && sessionId) {
        const playerCategoriesMap = {};
        active.forEach((p) => {
          playerCategoriesMap[p.id] = {
            mixed: newCategories.mixed[p.id] || null,
            levels: newCategories.levels[p.id] || null,
          };
        });
        const categoryRangesArray = [];
        const pushRanges = (meta, cohortType) => {
          const ranges = meta?.categoryRanges ? Object.entries(meta.categoryRanges) : [];
          ranges.forEach(([label, r]) => {
            if (r && typeof r.min === 'number' && typeof r.max === 'number') {
              categoryRangesArray.push({
                cohort_type: cohortType,
                category_label: label,
                min_elo: r.min,
                max_elo: r.max,
                player_count: r.size || 0,
              });
            }
          });
        };
        pushRanges(mixedMeta, 'mixed');
        pushRanges(maleMeta, 'levels_male');
        pushRanges(femaleMeta, 'levels_female');
        (async () => {
          try {
            const SessionStateService = await import('../services/sessionStateService');
            await SessionStateService.updatePlayerCategories(sessionId, playerCategoriesMap, categoryRangesArray);
          } catch (err) {
            console.error('Failed to save player categories:', err);
          }
        })();
      }
    } finally {
      setIsCategoryRefreshing(false);
    }
  }, [
    categorizationEnabled,
    getCategorizationPlayers,
    buildCategoryMetadata,
    buildCombinedLevelsMetadata,
    shouldRecalculateCategories,
    captureEloSnapshot,
    sessionLogger,
    sessionId,
  ]);

  const handleManualCategoryRefresh = useCallback(() => {
    if (!categorizationEnabled) return;
    const avg = computeAverageGamesPlayed();
    lastCategoryAutoRefreshThresholdRef.current = Math.floor(avg / 3) * 3;
    calculatePlayerCategories(true);
  }, [categorizationEnabled, calculatePlayerCategories, computeAverageGamesPlayed]);

  const handleCategorizationToggle = useCallback(async () => {
    const next = !categorizationEnabled;
    categorizationEnabledRef.current = next;
    setCategorizationEnabled(next);
    if (next) {
      const avg = computeAverageGamesPlayed();
      lastCategoryAutoRefreshThresholdRef.current = Math.floor(avg / 3) * 3;
      calculatePlayerCategories(true);
    } else {
      lastCategoryAutoRefreshThresholdRef.current = 0;
      const empty = { mixed: {}, levels: {}, levels_male: {}, levels_female: {} };
      setPlayerCategories(empty);
      playerCategoriesRef.current = empty;
    }
    if (sessionId) {
      try {
        await supabase.from('sessions').update({ enable_player_categorisation: next }).eq('id', sessionId);
      } catch (err) {
        console.error('Failed to update session categorisation flag:', err);
        setCategorizationEnabled(!next);
        categorizationEnabledRef.current = !next;
      }
    }
  }, [categorizationEnabled, sessionId, computeAverageGamesPlayed, calculatePlayerCategories]);

  const toggleRankingVisibility = useCallback((key) => {
    setRankingVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const formatRelativeCategoryTime = useCallback((isoString) => {
    if (!isoString) return 'Not calculated yet';
    const ts = new Date(isoString);
    if (Number.isNaN(ts.getTime())) return 'Not calculated yet';
    const diff = Date.now() - ts.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hr ago`;
    return ts.toLocaleString();
  }, []);

  const activeCategorizationPlayerCount = useMemo(
    () => getCategorizationPlayers().length,
    [getCategorizationPlayers]
  );

  const categoryStatusText = useMemo(() => {
    if (!categorizationEnabled) return 'Player categorization is disabled for this session.';
    if (activeCategorizationPlayerCount < CATEGORY_MIN_ACTIVE_PLAYERS) {
      return `Need ${CATEGORY_MIN_ACTIVE_PLAYERS - activeCategorizationPlayerCount} more active players to categorize (min ${CATEGORY_MIN_ACTIVE_PLAYERS}).`;
    }
    const maleMeta = categoryMetadata.levels_male || {};
    const femaleMeta = categoryMetadata.levels_female || {};
    if (maleMeta.disabledReason === 'not_enough_players' && femaleMeta.disabledReason === 'not_enough_players') {
      return 'Not enough male or female players to create levels categories.';
    }
    if (categoryMetadata.mixed.disabledReason === 'spread_too_small') {
      return 'Mixed ELO spread is too small to form categories.';
    }
    const lastCalc = categoryMetadata.mixed.lastCalculated || maleMeta.lastCalculated || femaleMeta.lastCalculated;
    if (!lastCalc) return 'Awaiting first category calculation.';
    return `Last calculated ${formatRelativeCategoryTime(lastCalc)}`;
  }, [categorizationEnabled, activeCategorizationPlayerCount, categoryMetadata, formatRelativeCategoryTime]);

  const categorySummaryText = useMemo(() => {
    if (!categorizationEnabled) return '';
    const mixedCount = categoryMetadata.mixed.categoryCount || 0;
    const maleCount = categoryMetadata.levels_male.categoryCount || 0;
    const femaleCount = categoryMetadata.levels_female.categoryCount || 0;
    if (mixedCount === 0 && maleCount === 0 && femaleCount === 0) return '';
    const parts = [];
    if (mixedCount > 0) parts.push(`Mixed: ${mixedCount}`);
    parts.push(`Male levels: ${maleCount || '--'}`);
    parts.push(`Female levels: ${femaleCount || '--'}`);
    return parts.join(' • ');
  }, [categorizationEnabled, categoryMetadata]);

  const lastCategoryCalculation =
    categoryMetadata.mixed.lastCalculated ||
    categoryMetadata.levels_male.lastCalculated ||
    categoryMetadata.levels_female.lastCalculated ||
    null;

  const buildRankingDataset = useCallback(
    (genderFilter, categoryGetter, sortValueGetter) => {
      const playersList = Object.values(playersById || {});
      const categorised = [];
      const uncategorised = [];
      playersList.forEach((player) => {
        if (!sessionPlayerStatus[player.id]) return;
        if (genderFilter && !genderFilter(player)) return;
        const gender = (player.gender || '').toLowerCase();
        const levelsCategory =
          gender === 'female'
            ? playerCategories.levels_female?.[player.id] || '--'
            : gender === 'male'
            ? playerCategories.levels_male?.[player.id] || '--'
            : playerCategories.levels?.[player.id] || '--';
        const entry = {
          id: player.id,
          name: player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim() || 'Unknown Player',
          mixedElo: player.mixed_elo_rating ?? null,
          levelsElo: player.levels_elo_rating ?? null,
          gamesPlayed: player.gamesPlayedThisSession || 0,
          mixedCategory: playerCategories.mixed?.[player.id] || '--',
          levelsCategory,
          gender,
        };
        const categoryValue = categoryGetter(entry);
        const sortValue = sortValueGetter(entry) ?? 0;
        const target = categoryValue && categoryValue !== '--' ? categorised : uncategorised;
        target.push({ ...entry, category: categoryValue || '--', sortValue });
      });
      const sortFn = (a, b) => (b.sortValue || 0) - (a.sortValue || 0);
      categorised.sort(sortFn);
      uncategorised.sort(sortFn);
      return { categorised, uncategorised };
    },
    [playersById, sessionPlayerStatus, playerCategories]
  );

  const mixedRanking = useMemo(
    () => buildRankingDataset(() => true, (e) => e.mixedCategory, (e) => e.mixedElo ?? e.levelsElo ?? 0),
    [buildRankingDataset]
  );
  const maleRanking = useMemo(
    () => buildRankingDataset(
      (p) => (p.gender || '').toLowerCase() === 'male',
      (e) => e.levelsCategory,
      (e) => e.levelsElo ?? e.mixedElo ?? 0
    ),
    [buildRankingDataset]
  );
  const femaleRanking = useMemo(
    () => buildRankingDataset(
      (p) => (p.gender || '').toLowerCase() === 'female',
      (e) => e.levelsCategory,
      (e) => e.levelsElo ?? e.mixedElo ?? 0
    ),
    [buildRankingDataset]
  );

  const mixColumns = useMemo(
    () => [
      { key: 'mixedElo', label: 'Mixed ELO', render: (row) => formatEloValue(row.mixedElo) },
      { key: 'mixedCategory', label: 'Mixed Cat', render: (row) => row.mixedCategory || '—' },
      { key: 'gamesPlayed', label: 'Games', render: (row) => row.gamesPlayed ?? 0 },
    ],
    []
  );
  const levelColumns = useMemo(
    () => [
      { key: 'levelsElo', label: 'Levels ELO', render: (row) => formatEloValue(row.levelsElo) },
      { key: 'levelsCategory', label: 'Levels Cat', render: (row) => row.levelsCategory || '—' },
      { key: 'gamesPlayed', label: 'Games', render: (row) => row.gamesPlayed ?? 0 },
    ],
    []
  );

  const rankingSections = useMemo(
    () => [
      {
        key: 'mixed',
        title: 'Mixed Doubles Ranking',
        description: 'All active players, prioritised by mixed-category tiers.',
        data: mixedRanking,
        meta: categoryMetadata.mixed,
        columns: mixColumns,
        categoryKey: 'mixedCategory',
        emptyLabel: 'No mixed category information available yet.',
      },
      {
        key: 'male',
        title: "Men's Levels Ranking",
        description: 'Only male players, grouped by their levels category.',
        data: maleRanking,
        meta: categoryMetadata.levels_male,
        columns: levelColumns,
        categoryKey: 'levelsCategory',
        emptyLabel: 'Not enough male players have been categorised.',
      },
      {
        key: 'female',
        title: "Women's Levels Ranking",
        description: 'Only female players, grouped by their levels category.',
        data: femaleRanking,
        meta: categoryMetadata.levels_female,
        columns: levelColumns,
        categoryKey: 'levelsCategory',
        emptyLabel: 'Not enough female players have been categorised.',
      },
    ],
    [mixedRanking, maleRanking, femaleRanking, categoryMetadata, mixColumns, levelColumns]
  );

  const rankingSectionsMap = useMemo(() => {
    const map = {};
    rankingSections.forEach((s) => { map[s.key] = s; });
    return map;
  }, [rankingSections]);

  const rankingTabOptions = useMemo(
    () => [
      { key: 'mixed', label: 'Mixed Doubles' },
      { key: 'male', label: "Men's Levels" },
      { key: 'female', label: "Women's Levels" },
    ],
    []
  );

  const currentRankingSection = rankingSectionsMap[rankingTab] || rankingSections[0] || null;

  const getSectionStatusText = useCallback((meta, totalPlayers) => {
    if (!meta) return '';
    if (meta.categoryCount > 0) {
      const methodLabel = meta.method ? ` · method: ${meta.method.replace(/_/g, ' ')}` : '';
      const playerLabel = totalPlayers ? ` · ${totalPlayers} players` : '';
      return `${meta.categoryCount} categories${playerLabel}${methodLabel}`;
    }
    switch (meta.disabledReason) {
      case 'not_enough_players': return 'Not enough players to build categories.';
      case 'spread_too_small': return 'ELO spread was too small to create distinct tiers.';
      case 'not_calculated': return 'Awaiting category calculation.';
      default: return 'No categories calculated yet.';
    }
  }, []);

  const renderCategoryChips = useCallback((meta) => {
    if (!meta?.categoryRanges) return null;
    const labels = Object.keys(meta.categoryRanges);
    if (labels.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {labels.sort().map((label) => {
          const range = meta.categoryRanges[label] || {};
          const hasRange = typeof range.min === 'number' && typeof range.max === 'number';
          const rangeText = hasRange ? `${formatEloValue(range.min)}–${formatEloValue(range.max)}` : '—';
          const sizeText = range.size ? ` · ${range.size}` : '';
          return (
            <span
              key={`${label}-${rangeText}`}
              className="inline-flex items-center gap-1 rounded-full bg-surface-subtle px-2.5 py-1 text-xs text-text-primary"
            >
              <span className="font-semibold">{label}</span>
              <span>{rangeText}{sizeText}</span>
            </span>
          );
        })}
      </div>
    );
  }, []);

  const renderRankingSection = useCallback(
    ({ key, title, description, data, meta, columns, categoryKey, emptyLabel }) => {
      const dataset = data || { categorised: [], uncategorised: [] };
      const showAllRows = rankingVisibility[key];
      const hiddenCount = dataset.uncategorised.length;
      const rows = showAllRows ? [...dataset.categorised, ...dataset.uncategorised] : dataset.categorised;
      const statusText = getSectionStatusText(meta, dataset.categorised.length + dataset.uncategorised.length);
      const hasRows = rows.length > 0;

      return (
        <div key={key} className="bg-surface border border-border-medium rounded-xl p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h4 className="text-base font-semibold text-text-primary">{title}</h4>
              <p className="text-sm text-text-secondary">{description}</p>
              <p className="text-xs text-text-muted mt-1">{statusText}</p>
              {renderCategoryChips(meta)}
            </div>
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => toggleRankingVisibility(key)}
                className="text-sm text-primary hover:text-primary-dark"
              >
                {showAllRows ? 'Hide uncategorised players' : `Show ${hiddenCount} uncategorised`}
              </button>
            )}
          </div>
          <div className="mt-4">
            {!hasRows ? (
              <div className="p-4 border border-dashed border-border-medium rounded-lg text-sm text-text-secondary">
                {dataset.categorised.length === 0 && hiddenCount > 0 && !showAllRows
                  ? 'No players with a category yet. Show uncategorised players to review everyone.'
                  : emptyLabel}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border-medium text-sm">
                  <thead className="bg-surface-subtle">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-text-muted">#</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-text-muted">Player</th>
                      {columns.map((col) => (
                        <th key={col.key} className="px-3 py-2 text-left text-xs font-semibold text-text-muted">{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-medium">
                    {rows.map((row, index) => {
                      const categoryValue = row[categoryKey] || '--';
                      const isCategorised = categoryValue !== '--';
                      const prevRow = rows[index - 1];
                      const prevCategory = prevRow ? prevRow[categoryKey] : null;
                      const showDivider = isCategorised && categoryValue !== prevCategory;
                      return (
                        <tr
                          key={`${row.id}-${categoryKey}-${index}`}
                          className={`${!isCategorised ? 'opacity-70' : ''} ${showDivider ? 'border-t-2 border-border-medium' : ''}`}
                        >
                          <td className="px-3 py-2 text-sm text-text-muted">{index + 1}</td>
                          <td className={`px-3 py-2 font-medium ${getGenderTextColor(row.gender)}`}>{row.name}</td>
                          {columns.map((col) => (
                            <td key={col.key} className="px-3 py-2 text-sm text-text-primary">{col.render(row)}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      );
    },
    [rankingVisibility, toggleRankingVisibility, getSectionStatusText, renderCategoryChips]
  );

  const restoreFromSavedState = useCallback((savedState) => {
    if (!savedState?.playerCategories) return;
    const byId = savedState.playerCategories;
    const mixed = {};
    const levels = {};
    const levels_male = {};
    const levels_female = {};
    if (byId && typeof byId === 'object' && !byId.mixed && !byId.levels) {
      Object.entries(byId).forEach(([playerId, cat]) => {
        if (cat?.mixed) mixed[playerId] = cat.mixed;
        if (cat?.levels) {
          levels[playerId] = cat.levels;
          const player = playersById[playerId];
          if (player?.gender === 'male') levels_male[playerId] = cat.levels;
          else if (player?.gender === 'female') levels_female[playerId] = cat.levels;
        }
      });
    } else {
      Object.assign(mixed, byId.mixed || {});
      Object.assign(levels, byId.levels || {});
      Object.assign(levels_male, byId.levels_male || {});
      Object.assign(levels_female, byId.levels_female || {});
    }
    const categories = { mixed, levels, levels_male, levels_female };
    setPlayerCategories(categories);
    playerCategoriesRef.current = categories;

    if (savedState.category_metadata) {
      const meta = savedState.category_metadata;
      setCategoryMetadata({
        mixed: meta.mixed || createDefaultCategoryMeta(),
        levels: meta.levels || createDefaultCategoryMeta(),
        levels_male: meta.levels_male || createDefaultCategoryMeta(),
        levels_female: meta.levels_female || createDefaultCategoryMeta(),
      });
    } else if (savedState.categoryRanges?.length > 0) {
      const mixedRanges = [];
      const levelsMaleRanges = [];
      const levelsFemaleRanges = [];
      savedState.categoryRanges.forEach((r) => {
        const obj = { label: r.category_label, min: r.min_elo, max: r.max_elo, size: r.player_count };
        if (r.cohort_type === 'mixed') mixedRanges.push(obj);
        else if (r.cohort_type === 'levels_male') levelsMaleRanges.push(obj);
        else if (r.cohort_type === 'levels_female') levelsFemaleRanges.push(obj);
      });
      const toMeta = (ranges) => (ranges.length > 0
        ? { ...createDefaultCategoryMeta(), categoryRanges: Object.fromEntries(ranges.map((r) => [r.label, { min: r.min, max: r.max, size: r.size }])), categoryCount: ranges.length, disabledReason: null }
        : createDefaultCategoryMeta());
      const mixedMeta = toMeta(mixedRanges);
      const maleMeta = toMeta(levelsMaleRanges);
      const femaleMeta = toMeta(levelsFemaleRanges);
      setCategoryMetadata({
        mixed: mixedMeta,
        levels: buildCombinedLevelsMetadata(maleMeta, femaleMeta),
        levels_male: maleMeta,
        levels_female: femaleMeta,
      });
    }
  }, [playersById]);

  return {
    categorizationEnabled,
    setCategorizationEnabled,
    playerCategories,
    setPlayerCategories,
    playerCategoriesRef,
    categoryMetadata,
    eloChangeTracker,
    isCategoryRefreshing,
    rankingTab,
    setRankingTab,
    rankingVisibility,
    rankingTabOptions,
    rankingSections,
    rankingSectionsMap,
    currentRankingSection,
    activeCategorizationPlayerCount,
    categoryStatusText,
    categorySummaryText,
    lastCategoryCalculation,
    formatRelativeCategoryTime,
    getCategorizationPlayers,
    calculatePlayerCategories,
    shouldRecalculateCategories,
    handleCategorizationToggle,
    handleManualCategoryRefresh,
    renderRankingSection,
    restoreFromSavedState,
    categorizationEnabledRef,
  };
}

export { CATEGORY_MIN_ACTIVE_PLAYERS, CATEGORY_ELO_DELTA_THRESHOLD };
